import { FC, useEffect, useRef, useState } from "react";
import { Tooltip, CircularProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faPenToSquare,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import styled from "styled-components/macro";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { getTestOrderDisplay } from "../../utils/testOrderDisplay";
import { NewRequisitionType, DRAFT_STATUS } from "../NewRequisition/types";
import {
  TestOrderPrimary,
  TestOrderSecondary,
  TestOrderStack,
} from "../NewRequisition/NewRequisition.styles";
import { submitNewRequisitionOrder } from "../NewRequisition/Services/NewReqService";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import {
  RedrawTable,
  RedrawTableHeader,
  RedrawRow,
  RedrawCell,
  RedrawActionsCell,
  RedrawIconButton,
  PlaceAllButton,
  ViewOrdersButton,
  HighlightText,
  ErrorMessage,
  TooltipText,
  FooterWrapper,
  FooterActions,
} from "./RedrawTable.styles";

const getRedrawReason = (order: NewRequisitionType) => {
  const display = getTestOrderDisplay({
    isCarrierScreenOrder: order.is_carrier_screen_order,
    isAneuploidyOrder: order.is_aneuploidy_order,
    expandedCarrierPanel: order.expanded_carrier_panel,
    isFragileX: order.is_fragile_x,
    isAcogPlus: order.is_acog_plus,
    isAneuploidyOptOutFetalSex: order.is_aneuploidy_opt_out_fetal_sex,
    isAneuploidyIncludeRhd: order.is_aneuploidy_include_rhd,
    isFetalAntigen: order.is_fetal_antigen,
  });

  return {
    primary: display.primary ? `Redraw - ${display.primary}` : "Redraw",
    secondary: display.secondary,
  };
};

export const RedrawMessage: FC = () => (
  <>
    Please verify the clinical information for the following orders by clicking
    on the edit pencil. Confirm <HighlightText>gestation type</HighlightText>,{" "}
    <HighlightText>egg donor/gestational carrier status</HighlightText>, and{" "}
    <HighlightText>known vanishing twin information</HighlightText> to ensure
    accurate reporting.
  </>
);

interface UseRedrawTableStateArgs {
  redrawDrafts: NewRequisitionType[];
  onRowClick: (patientName: string) => void;
  resetSignal?: unknown;
}

interface UseRedrawTableStateResult {
  isPlaced: (order: NewRequisitionType) => boolean;
  loadingId: string | null;
  bulkLoading: boolean;
  errorMessage: string | null;
  pendingOrders: NewRequisitionType[];
  allPlaced: boolean;
  placeOrder: (order: NewRequisitionType) => Promise<void>;
  placeAllOrders: () => Promise<void>;
  handleRowClick: (order: NewRequisitionType) => void;
  rowRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  isBusy: boolean;
}

export const useRedrawTableState = ({
  redrawDrafts,
  onRowClick,
  resetSignal,
}: UseRedrawTableStateArgs): UseRedrawTableStateResult => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [placedOrderIds, setPlacedOrderIds] = useState<Set<string>>(new Set());
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (loadingId && bulkLoading) {
      const rowEl = rowRefs.current.get(loadingId);
      rowEl?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loadingId, bulkLoading]);

  useEffect(() => {
    setPlacedOrderIds(new Set());
    setLoadingId(null);
    setBulkLoading(false);
    setErrorMessage(null);
  }, [resetSignal]);

  const isPlaced = (order: NewRequisitionType) =>
    placedOrderIds.has(order.unity_complete_id) ||
    order.draft_status === DRAFT_STATUS.SUBMITTED_REDRAW;

  const pendingOrders = redrawDrafts.filter((o) => !isPlaced(o));
  const allPlaced = pendingOrders.length === 0 && redrawDrafts.length > 0;

  const placeOrder = async (order: NewRequisitionType) => {
    if (!auth?.token) return;
    setLoadingId(order.unity_complete_id);
    setErrorMessage(null);
    try {
      const result = await submitNewRequisitionOrder(
        order,
        auth.token,
        setLoggedOut,
      );
      if (result) {
        TrackAnalyticsEvent(
          "RedrawDraftOrderSubmitted",
          auth.token,
          auth.username,
          {
            test_type: order.is_carrier_screen_order ? "carrier" : "aneuploidy",
            lims_id: order.lims_id || "",
            npi: order.npi || "",
          },
        );
        setPlacedOrderIds((prev) => new Set(prev).add(order.unity_complete_id));
      } else {
        setErrorMessage("Failed to place the order. Please try again.");
      }
    } catch {
      setErrorMessage("Failed to place the order. Please try again.");
    } finally {
      setLoadingId(null);
    }
  };

  const placeAllOrders = async () => {
    if (!auth?.token) return;
    setBulkLoading(true);
    setErrorMessage(null);
    const totalToPlace = pendingOrders.length;
    let failedCount = 0;
    for (const order of pendingOrders) {
      setLoadingId(order.unity_complete_id);
      try {
        const result = await submitNewRequisitionOrder(
          order,
          auth.token,
          setLoggedOut,
        );
        if (result) {
          TrackAnalyticsEvent(
            "RedrawDraftOrderSubmitted",
            auth.token,
            auth.username,
            {
              test_type: order.is_carrier_screen_order
                ? "carrier"
                : "aneuploidy",
              lims_id: order.lims_id || "",
              npi: order.npi || "",
            },
          );
          setPlacedOrderIds((prev) =>
            new Set(prev).add(order.unity_complete_id),
          );
        } else {
          failedCount += 1;
        }
      } catch {
        failedCount += 1;
      }
    }
    setLoadingId(null);
    setBulkLoading(false);
    if (failedCount > 0) {
      setErrorMessage(
        `Failed to place ${failedCount} of ${totalToPlace} orders. Please try again. If the issue persists, try one by one.`,
      );
    }
  };

  const handleRowClick = (order: NewRequisitionType) => {
    if (bulkLoading || loadingId) return;
    if (isPlaced(order)) {
      onRowClick(`${order.first_name} ${order.last_name}`);
    }
  };

  return {
    isPlaced,
    loadingId,
    bulkLoading,
    errorMessage,
    pendingOrders,
    allPlaced,
    placeOrder,
    placeAllOrders,
    handleRowClick,
    rowRefs,
    isBusy: bulkLoading || loadingId !== null,
  };
};

const FlushTable = styled(RedrawTable)`
  margin: 0;
`;

interface RedrawTableViewProps {
  redrawDrafts: NewRequisitionType[];
  state: UseRedrawTableStateResult;
  onEditOrder?: (order: NewRequisitionType) => void;
  onDismiss?: (order: NewRequisitionType) => void;
  flushMargins?: boolean;
}

export const RedrawTableView: FC<RedrawTableViewProps> = ({
  redrawDrafts,
  state,
  onEditOrder,
  onDismiss,
  flushMargins,
}) => {
  const TableComponent = flushMargins ? FlushTable : RedrawTable;

  return (
    <TableComponent role="table" aria-label="Redraw requested orders">
      <RedrawTableHeader role="row">
        <RedrawCell role="columnheader" $heading>
          Patient
        </RedrawCell>
        <RedrawCell role="columnheader" $heading>
          Reason
        </RedrawCell>
        <RedrawCell role="columnheader" $heading>
          Actions
        </RedrawCell>
      </RedrawTableHeader>
      {redrawDrafts.map((order) => {
        const placed = state.isPlaced(order);
        const loading = state.loadingId === order.unity_complete_id;
        const patientName = `${order.first_name} ${order.last_name}`;
        const redrawReason = getRedrawReason(order);
        const placeOrderLabel = loading
          ? `Placing redraw order for ${patientName}`
          : `Place redraw order for ${patientName}`;
        return (
          <RedrawRow
            key={order.unity_complete_id}
            role="row"
            ref={(el) => {
              if (el) state.rowRefs.current.set(order.unity_complete_id, el);
            }}
            $placed={placed}
            onClick={() => state.handleRowClick(order)}
            onKeyDown={(event) => {
              if (!placed) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                state.handleRowClick(order);
              }
            }}
            tabIndex={placed && !state.isBusy ? 0 : undefined}
            aria-label={
              placed ? `View placed redraw order for ${patientName}` : undefined
            }
          >
            <RedrawCell role="cell" $placed={placed}>
              {patientName}
            </RedrawCell>
            <RedrawCell role="cell" $placed={placed}>
              <TestOrderStack style={{ color: "inherit" }}>
                <TestOrderPrimary>{redrawReason.primary}</TestOrderPrimary>
                {redrawReason.secondary && (
                  <TestOrderSecondary>
                    {redrawReason.secondary}
                  </TestOrderSecondary>
                )}
              </TestOrderStack>
            </RedrawCell>
            <RedrawActionsCell role="cell">
              {!placed && onDismiss && (
                <Tooltip
                  title={<TooltipText>Remove this alert</TooltipText>}
                  arrow
                  placement="top"
                  enterDelay={500}
                  leaveDelay={0}
                  disableInteractive
                >
                  <span>
                    <RedrawIconButton
                      aria-label={`Dismiss redraw alert for ${patientName}`}
                      size="small"
                      disabled={state.isBusy}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (state.isBusy) return;
                        onDismiss(order);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </RedrawIconButton>
                  </span>
                </Tooltip>
              )}
              {!placed && (
                <Tooltip
                  title={<TooltipText>Edit draft</TooltipText>}
                  arrow
                  placement="top"
                  enterDelay={500}
                  leaveDelay={0}
                  disableInteractive
                >
                  <span>
                    <RedrawIconButton
                      aria-label={`Edit redraw order for ${patientName}`}
                      size="small"
                      disabled={state.isBusy}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (state.isBusy) return;
                        if (onEditOrder) onEditOrder(order);
                      }}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </RedrawIconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip
                title={
                  <TooltipText>
                    {placed ? "Order placed" : "Place this order"}
                  </TooltipText>
                }
                arrow
                placement="top"
                enterDelay={500}
                leaveDelay={0}
                disableInteractive
              >
                <span>
                  <RedrawIconButton
                    aria-label={
                      placed
                        ? `Redraw order placed for ${patientName}`
                        : placeOrderLabel
                    }
                    aria-busy={loading || undefined}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!placed && !loading && !state.bulkLoading)
                        state.placeOrder(order);
                    }}
                    disabled={
                      placed || state.loadingId !== null || state.bulkLoading
                    }
                  >
                    {loading && <CircularProgress aria-hidden size={14} />}
                    {!loading && placed && (
                      <FontAwesomeIcon aria-hidden icon={faCircleCheck} />
                    )}
                    {!loading && !placed && (
                      <FontAwesomeIcon aria-hidden icon={faCircleCheck} />
                    )}
                  </RedrawIconButton>
                </span>
              </Tooltip>
            </RedrawActionsCell>
          </RedrawRow>
        );
      })}
    </TableComponent>
  );
};

interface RedrawFooterProps {
  state: UseRedrawTableStateResult;
  onViewOrders: () => void;
}

export const RedrawFooter: FC<RedrawFooterProps> = ({
  state,
  onViewOrders,
}) => (
  <FooterWrapper>
    {state.errorMessage && (
      <ErrorMessage role="alert">{state.errorMessage}</ErrorMessage>
    )}
    <FooterActions>
      {state.allPlaced ? (
        <ViewOrdersButton onClick={onViewOrders}>VIEW ORDERS</ViewOrdersButton>
      ) : (
        <PlaceAllButton
          onClick={state.placeAllOrders}
          disabled={state.isBusy || state.pendingOrders.length === 0}
        >
          PLACE ALL ORDERS
        </PlaceAllButton>
      )}
    </FooterActions>
  </FooterWrapper>
);
